// ─── AM-side Saved Options Panel (lives in ClientDetailDrawer) ───
import React, { useEffect, useState } from 'react';
import { C, S } from '../../utils/styles';
import Icon from '../Icon';
import { fmt } from '../../utils/formatters';
import api from '../../utils/api';

export default function SavedOptionsAdminPanel({ client, onSave }) {
  const savedOptions = client?.savedOptions || null;
  const [createdByName, setCreatedByName] = useState(null);
  const [selectedByName, setSelectedByName] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!savedOptions) return undefined;
    (async () => {
      try {
        const list = await api.users.list();
        if (savedOptions.createdById) {
          const m = list.find((u) => u.id === savedOptions.createdById || u._id === savedOptions.createdById);
          if (m && !cancelled) setCreatedByName(`${m.firstName || ''} ${m.lastName || ''}`.trim() || m.email);
        }
        if (savedOptions.selectedBy) {
          const m = list.find((u) => u.id === savedOptions.selectedBy || u._id === savedOptions.selectedBy);
          if (m && !cancelled) setSelectedByName(`${m.firstName || ''} ${m.lastName || ''}`.trim() || m.email);
        }
      } catch (err) {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, [savedOptions?.createdById, savedOptions?.selectedBy]);

  if (!savedOptions || !savedOptions.options || savedOptions.options.length === 0) return null;

  const isSelected = !!savedOptions.selectedOptionId;
  const selectedOpt = isSelected ? savedOptions.options.find((o) => o.id === savedOptions.selectedOptionId) : null;

  const removeOptions = async () => {
    if (!window.confirm('Remove saved options from the employer portal? They will no longer see the comparison view.')) return;
    setBusy(true);
    try {
      await onSave({ ...client, savedOptions: null });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="am-saved-options-panel" style={{ ...S.card, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <Icon name="bookmark-outline" style={{ fontSize: 16, color: C.accent }} />
        <h3 style={{ ...S.heading, margin: 0 }}>Saved Options</h3>
      </div>

      <div style={{ fontSize: 12, color: C.textMid, lineHeight: 1.7, marginBottom: 12 }}>
        <div>
          <strong>Status:</strong>{' '}
          {isSelected ? (
            <span style={{ color: C.green, fontWeight: 700 }}>
              ✓ Employer selected {selectedOpt?.label || 'an option'}
              {savedOptions.selectedAt ? ` on ${formatDate(savedOptions.selectedAt)}` : ''}
              {selectedByName ? ` by ${selectedByName}` : ''}
            </span>
          ) : (
            <span>{savedOptions.options.length} options saved · Pending selection</span>
          )}
        </div>
        <div>
          <strong>Sent:</strong> {formatDate(savedOptions.createdAt)}
          {createdByName ? ` by ${createdByName}` : ''} ·{' '}
          <span>Source: {savedOptions.createdBy === 'account_manager' ? 'Account manager' : 'Employer self-serve'}</span>
        </div>
        {(savedOptions.versions && savedOptions.versions.length > 0) && (
          <div>
            <strong>Versions:</strong> {savedOptions.versions.length + 1} (latest shown to employer)
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
        {savedOptions.options.map((opt) => (
          <div
            key={opt.id}
            data-testid={`am-option-row-${opt.id}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 12px',
              background: opt.id === savedOptions.selectedOptionId ? '#ecfdf5' : C.bg,
              border: `1px solid ${opt.id === savedOptions.selectedOptionId ? C.green : C.borderLight}`,
              borderRadius: 8,
              fontSize: 12,
            }}
          >
            <span style={{ fontWeight: 800, color: C.text, flexShrink: 0 }}>{opt.label}:</span>
            <span style={{ color: C.text, flex: 1 }}>{opt.title}</span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: C.green }}>
              {fmt(opt.totalEmployerFica)} FICA
            </span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: C.text }}>
              {opt.section105 ? `${opt.section105.newEligibilityRate}%` : `${opt.section125.eligibilityRate}%`}
            </span>
            {opt.badge === 'recommended' && (
              <span style={{ fontSize: 9, fontWeight: 800, padding: '2px 6px', borderRadius: 4, background: '#dbeafe', color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                ⭐ Recommended
              </span>
            )}
            {opt.id === savedOptions.selectedOptionId && (
              <span style={{ fontSize: 9, fontWeight: 800, padding: '2px 6px', borderRadius: 4, background: C.green, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                ✓ Selected
              </span>
            )}
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {!isSelected && (
          <button
            type="button"
            onClick={removeOptions}
            disabled={busy}
            data-testid="am-remove-options-btn"
            style={{ padding: '8px 14px', background: 'transparent', color: C.textMid, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: busy ? 'not-allowed' : 'pointer' }}
          >
            <Icon name="trash-outline" style={{ fontSize: 12, marginRight: 4, verticalAlign: 'middle' }} />
            Remove options
          </button>
        )}
        {isSelected && (
          <button
            type="button"
            data-testid="am-proceed-implementation-btn"
            style={{ padding: '8px 16px', background: C.accent, color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 800, cursor: 'pointer' }}
          >
            <Icon name="rocket-outline" style={{ fontSize: 12, marginRight: 4, verticalAlign: 'middle' }} />
            Proceed to implementation
          </button>
        )}
        <span style={{ fontSize: 11, color: C.textLight, alignSelf: 'center' }}>
          To re-issue, run the Section 105 Plan Advisor again and click "Send to Employer Portal".
        </span>
      </div>
    </section>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
  } catch (err) {
    return '—';
  }
}
